import { useCallback, useState } from "react";
import { placementsAt, type BoundDefinitions, type ProcessCatalogue, type ToolSummary, type WorkflowDefinition } from "../../../api/content/processDefinitions";
import { readUploadWorkflow } from "../../../api/content/hostingApi";
import { listProjectItems } from "../../../api/content/authoring/processAuthoringApi";
import { listWorkflows, type WorkflowSummary } from "../../../api/content/workflowsApi";
import { toUiError } from "../../../errors/uiError";
import { useLoad, type LoadState } from "../../../shared/loading/useLoad";
import { hasNoContentYet } from "../../ontology/modelVersions";
import { useCorpusSelection } from "../corpus/corpusSelectionContext";
import { corpusTargetDraft, importTargetOf, type ImportTarget, type SegmentationTemplateName } from "./corpusImport";

/** What each import form gets from the import's preparation view (`ImportForms`). */
export interface ImportFormProps {
  readonly projectId: string;
  /** The corpus to import into first; `null` for the corpus worked in. */
  readonly corpusId: string | null;
  /** The server made the import's update Thread: the preparation turns into it (WA-12). */
  readonly onStarted: (threadId: string) => void;
}

/** The corpus an import goes into, as both import forms choose it (CorpusTargetFields); first `corpusId`, else the corpus worked in. */
export function useCorpusTarget(projectId: string, corpusId: string | null, template: SegmentationTemplateName) {
  const { corpora, selectedCorpus, selectCorpus, reloadCorpora } = useCorpusSelection();
  const existingCorpora = corpora?.status === "loaded" ? corpora.value : [];
  const [draft, setDraft] = useState(() => corpusTargetDraft(corpusId ?? selectedCorpus?.corpusId ?? null, template));
  const [segmentationInvalid, setSegmentationInvalid] = useState(false);
  const existingCorpusId = existingCorpora.some((candidate) => candidate.corpusId === draft.corpusId) ? draft.corpusId : null;
  return {
    draft,
    setDraft,
    reloadCorpora,
    fields: { draft, onChange: setDraft, corpora: existingCorpora, segmentationInvalid },
    /** The chosen existing corpus; `null` for a new one. */
    existingCorpusId,
    /** A new corpus still needs its name: a reason the import can't start yet. */
    nameMissing: existingCorpusId === null && draft.newName.trim() === "",
    /** The checked target; `null` while incomplete or when the segmentation is no JSON object, which it marks. */
    target(): ImportTarget | null {
      const target = importTargetOf(draft, existingCorpora, projectId);
      setSegmentationInvalid(target === "invalidSegmentation");
      return target === "invalidSegmentation" ? null : target;
    },
    /** After an import, its corpus is shown and is the next import's target, not another new one. */
    imported(corpusId: string, nextTemplate: SegmentationTemplateName) {
      selectCorpus(corpusId);
      setDraft(corpusTargetDraft(corpusId, nextTemplate));
    },
  };
}

/**
 * The import Workflows (F16): the Workflows the project runs that read the bound source
 * version (content.md D-18), its own and those of the libraries it pins, the ones it runs
 * first. A project without content has none, which the form says beside its button. The
 * chosen one is the first unless the person picks another.
 */
export function useImportWorkflow(projectId: string) {
  const load = useCallback(async (signal: AbortSignal): Promise<ImportWorkflows> => {
    try {
      const readers = (await listWorkflows(projectId, null, signal)).workflows.filter((workflow) => workflow.readsSourceVersion && !workflow.abstract);
      // The ones the project runs first, such as a new project's "Ingest text documents"; then other library Workflows.
      const candidates = [...readers.filter((workflow) => workflow.runsInProject), ...readers.filter((workflow) => !workflow.runsInProject)];
      // Content without items, opened for a free Thread, starts like none.
      return { startsEmpty: candidates.length === 0 && (await listProjectItems(projectId, null, signal)).items.length === 0, candidates };
    } catch (caught) {
      if (hasNoContentYet(toUiError(caught))) return { startsEmpty: true, candidates: [] };
      throw caught;
    }
  }, [projectId]);
  const loaded = useLoad(load);
  const [choice, setChoice] = useState("");
  const candidates = loaded.state.status === "loaded" ? loaded.state.value.candidates : [];
  const workflow = candidates.find((candidate) => candidate.reference === choice) ?? candidates[0] ?? null;
  return { loaded, candidates, workflow, choose: setChoice };
}

interface ImportWorkflows {
  /**
   * The project holds no items yet, so it may gain the installation's libraries for new
   * projects, as a new project does (module B refuses content with items).
   */
  readonly startsEmpty: boolean;
  readonly candidates: readonly WorkflowSummary[];
}

export type ImportWorkflowChoice = ReturnType<typeof useImportWorkflow>;

/**
 * The import Workflow uploads into an existing corpus run: the one its first upload pinned
 * (module B, `GET /corpora/{id}/source-imports`), which a later choice does not change.
 * `null` for a new corpus or one without uploads, where the choice decides.
 */
export function useUploadWorkflow(corpusId: string | null) {
  const load = useCallback((signal: AbortSignal) => (corpusId === null ? Promise.resolve(null) : readUploadWorkflow(corpusId, signal)), [corpusId]);
  return useLoad(load);
}

/** Why the import can't run with this Workflow choice yet; `null` once one is chosen or pinned. */
export function workflowBlocker(choice: ImportWorkflowChoice, pinned: LoadState<string | null> | null = null): "workflowsLoading" | "workflowsFailed" | "noContentYet" | "noImportWorkflow" | null {
  if (pinned !== null && pinned.status !== "loaded") return pinned.status === "failed" ? "workflowsFailed" : "workflowsLoading";
  if (pinned?.status === "loaded" && pinned.value !== null) return null;
  switch (choice.loaded.state.status) {
    case "loaded":
      if (choice.workflow !== null) return null;
      return choice.loaded.state.value.startsEmpty ? "noContentYet" : "noImportWorkflow";
    case "failed":
      return "workflowsFailed";
    case "loading":
      return "workflowsLoading";
  }
}

/**
 * The tools an import Workflow's steps run, its sub-workflows' included, each once, from the
 * catalogue the Workflow view reads (`loadProcess`).
 */
export function importToolsOf(workflow: WorkflowDefinition, catalogue: ProcessCatalogue): readonly ToolSummary[] {
  const tools = new Map<string, ToolSummary>();
  const visit = (definition: WorkflowDefinition, bound: BoundDefinitions | null, seen: ReadonlySet<string>) => {
    // Bound abstract steps run their bound definitions (ADR-0043 D5); an unbound one runs no tool.
    for (const use of placementsAt(definition, bound, catalogue)) {
      if (use.kind === "workflow") {
        const sub = catalogue.workflows.get(use.workflow);
        if (sub !== undefined && !seen.has(use.workflow)) visit(sub, use.definitions, new Set([...seen, use.workflow]));
        continue;
      }
      const tool = use.kind === "step" ? catalogue.steps.get(use.step)?.tool : undefined;
      const summary = tool === null || tool === undefined ? undefined : catalogue.tools.get(tool);
      if (summary !== undefined) tools.set(summary.reference, summary);
    }
  };
  visit(workflow, null, new Set([workflow.reference]));
  return [...tools.values()];
}
